import { MobilePhone } from 'components/Checkout/MobilePhone/MobilePhone';
import styles from './Checkout.module.scss';

import React, { useState, useEffect } from 'react';
import { Redirect, Route, Switch, RouteComponentProps } from 'react-router-dom';

import { isCartItemQuantityMax } from 'components/Cart/utils/Util';
import { redesignV2RoutePath } from 'constants/redesignV2';
import { CheckoutBasePage } from './CheckoutBasePage/CheckoutBasePage';
import { useCheckout } from './CheckoutProvider';
import { CheckoutOtpPage } from './CheckoutOtpPage/CheckoutOtpPage';
import { CheckoutInfoPage } from './CheckoutInfoPage/CheckoutInfoPage';
import { ErrorPageOtp } from 'components/ErrorPage/ErrorPageOtp';
import errorPageImageOtp from 'images/otp-error-image-lockup.svg';
import otpUnsuccessfulImage from 'images/otp-unsuccessful.svg';
import { useTranslation } from 'react-i18next';
import { useCartContext } from 'components/Cart/hooks/useCartContext';

export enum CheckoutRoutePath {
  INFO = '/info',
  STATUS = '/status',
  MOBILE_PHONE = '/mobile-phone',
  PASSCODE = '/passcode',
  OTP_ERROR = '/invalid-order',
  OTP_UNSUCCESSFUL = '/otp-unsuccessful',
}

export const ALL_CHECKOUT_PATHS = {
  root: redesignV2RoutePath.PURCHASE,
  info: `${redesignV2RoutePath.PURCHASE}${CheckoutRoutePath.INFO}`,
  status: `${redesignV2RoutePath.PURCHASE}${CheckoutRoutePath.STATUS}`,
  mobilePhone: `${redesignV2RoutePath.PURCHASE}${CheckoutRoutePath.MOBILE_PHONE}`,
  passcode: `${redesignV2RoutePath.PURCHASE}${CheckoutRoutePath.PASSCODE}`,
  otpError: `${redesignV2RoutePath.PURCHASE}${CheckoutRoutePath.OTP_ERROR}`,
  otpUnsuccessful: `${redesignV2RoutePath.PURCHASE}${CheckoutRoutePath.OTP_UNSUCCESSFUL}`,
};

const MAX_PHONE_ATTEMPTS = 3;

type CheckoutProps = RouteComponentProps<{}>;

export const Checkout: React.FC<CheckoutProps> = ({ location }) => {
  const { t } = useTranslation();
  const { cart } = useCartContext();
  const { errorState } = useCheckout();
  const { hasDuplicateAccountError, hasDuplicateLeadError, hasApiError } = errorState;
  const [isMaxOtpRequests, setIsMaxOtpRequests] = useState(false);
  const [phoneNumberChangeAttempts, setPhoneNumberChangeAttempts] = useState(0);

  const isCartEmpty = !cart.cartMetadata?.hardware.length && !cart.products.length;
  const isStatusPage = location.pathname === ALL_CHECKOUT_PATHS.status;
  const isMobilePhonePage = location.pathname === ALL_CHECKOUT_PATHS.mobilePhone;
  const isPasscodePath = location.pathname === ALL_CHECKOUT_PATHS.passcode;
  const shouldRedirectToCart =
    (isCartEmpty && !isStatusPage) ||
    isCartItemQuantityMax(cart.cartMetadata?.hardware) ||
    isCartItemQuantityMax(cart.products);

  const isBackButtonVisible = isMobilePhonePage || isPasscodePath;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const otpErrorTitle = hasDuplicateAccountError
    ? t('otp.errors.duplicateAccount.title')
    : t('otp.errors.duplicateLead.title');

  const otpErrorSubtitle = hasDuplicateAccountError
    ? t('otp.errors.duplicateAccount.subtitle')
    : t('otp.errors.duplicateLead.subtitle');

  const contactSalesText = t('contact-sales');

  return (
    <section className={styles.checkoutWrapper}>
      <CheckoutBasePage isBackButtonVisible={isBackButtonVisible}>
        <Switch>
          {shouldRedirectToCart && <Redirect to={redesignV2RoutePath.CART} />}
          <Route exact path={ALL_CHECKOUT_PATHS.info}>
            <CheckoutInfoPage />
          </Route>
          <Route exact path={ALL_CHECKOUT_PATHS.mobilePhone}>
            <MobilePhone
              isMaxOtpRequests={isMaxOtpRequests}
              setIsMaxOtpRequests={setIsMaxOtpRequests}
              setPhoneNumberChangeAttempts={setPhoneNumberChangeAttempts}
            />
          </Route>
          <Route exact path={ALL_CHECKOUT_PATHS.passcode}>
            <CheckoutOtpPage
              setIsMaxOtpRequests={setIsMaxOtpRequests}
              isMaxPhoneAttempts={phoneNumberChangeAttempts >= MAX_PHONE_ATTEMPTS}
            />
          </Route>
          <Route exact path={ALL_CHECKOUT_PATHS.otpUnsuccessful}>
            <ErrorPageOtp
              title={t('otp.unsuccessful.title')}
              subtitle={t('otp.unsuccessful.subtitle')}
              buttonText={contactSalesText}
              imageUrl={otpUnsuccessfulImage}
              hasSignIn={false}
            />
          </Route>

          <Route exact path={ALL_CHECKOUT_PATHS.otpError}>
            {!hasDuplicateAccountError && !hasDuplicateLeadError && !hasApiError && (
              <Redirect to={redesignV2RoutePath.CART} />
            )}
            <ErrorPageOtp
              title={otpErrorTitle}
              subtitle={otpErrorSubtitle}
              buttonText={contactSalesText}
              imageUrl={errorPageImageOtp}
              hasSignIn={hasDuplicateAccountError}
            />
          </Route>
        </Switch>
      </CheckoutBasePage>
    </section>
  );
};

Checkout.displayName = 'Checkout';
